import { RxDocument } from 'rxdb'
import { DateTime } from 'luxon'
import { Attr, BaseSchema, ModelSchema } from './helpers'
import { persistAttributeValueChanges, useAttributeOptions } from './hooks'
import { UrlSchema } from './UrlSchema'
import { AttributeOptionDoc, AttributeOptionSchema } from './AttributeOptionSchema'
import { AttributeValueSchema } from './AttributeValueSchema'
import { PageDoc } from './PageSchema'
import { UserSchema } from './UserSchema'

export enum AttributeType {
  'group' = 0,
  'text' = 1,
  'formula' = 2,
  'option' = 3,
  'linkedpage' = 4,
  'linkeduser' = 5,
  'number' = 6,
  'date' = 7,
  'boolean' = 8,
}

@ModelSchema({
  name: 'attribute',
  description: 'A type of attribute that can share validation rules between pages',
  version: 0,
  required: ['label', 'key', 'owner_id'],
  associations: () => [
    { type: 'belongsTo', model: UrlSchema, key: 'owner', foreignKey: 'owner_id' },
    { type: 'hasMany', model: AttributeOptionSchema, key: 'options' },
  ],
  lifecycle: {
    async fetch(store, params) {
      const { selector } = params
      if (selector.uuid) {
        const { response } = await store.API.GET(`/attributes/${selector.uuid}`, {}, params.modifier)
        return response.attribute
      }
    },

    async fetchQuery(store) {
      const { response } = await store.API.GET('/attributes')
      return response.attributes || []
    },
  },
})
export class AttributeSchema extends BaseSchema {
  static get HOOKS() {
    return {
      persistAttributeValueChanges,
      useAttributeOptions,
    }
  }

  // Useful config for individual attribute types
  static TYPE_CONFIG: {
    [key: string]: {
      parseRawValue?: (val: any) => any // Take an individual change, and convert it to a raw foreignKey string or value
      prepFormValue?: (attr: AttributeDoc, val: AttributeValueSchema[]) => any
      prepValue?: (val: any) => any // Take any value and prepare it to be an array of changes
      valueKey: keyof AttributeValueSchema // The key on the AttributeValue mode to store the value on
      label: string
      icon: string
    }
  } = {
    [AttributeType.text]: {
      label: 'Text',
      valueKey: 'value',
      icon: 'font_download',
      prepFormValue(attr, vals) {
        if (vals.length === 0) { return '' }
        return vals[0].value
      },
      prepValue: (v: string|string[]) => Array.isArray(v) ? v : [v],
    },
    [AttributeType.formula]: {
      icon: 'code',
      label: 'Formula',
      prepValue: (v: string|string[]) => Array.isArray(v) ? v : [v],
      valueKey: 'value',
    },
    [AttributeType.option]: {
      icon: 'arrow_drop_down_circle',
      label: 'Tags',
      parseRawValue: (opt: AttributeOptionDoc|string) => typeof opt ==='string' ? opt : opt.id,
      valueKey: 'option_id',
    },
    [AttributeType.linkedpage]: {
      icon: 'file_copy',
      label: 'Page',
      parseRawValue: (opt: PageDoc) => opt.id,
      prepValue: (v: PageDoc|PageDoc[]) => Array.isArray(v) ? v : [v],
      valueKey: 'linked_page_id',
    },
    [AttributeType.linkeduser]: {
      icon: 'person',
      label: 'User',
      parseRawValue: (opt: UserSchema) => opt.id,
      prepValue: (v: UserSchema|UserSchema[]) => Array.isArray(v) ? v : [v],
      valueKey: 'linked_user_id',
    },
    [AttributeType.number]: {
      label: 'Number',
      valueKey: 'value_num',
      icon: 'exposure_plus_1',
      parseRawValue: (d: string) => parseFloat(d),
      prepValue: (v: string|string[]) => Array.isArray(v) ? v : [v],
      prepFormValue(attr, vals) {
        if (vals.length === 0) { return '' }
        return vals[0].value_num || 0
      },
    },
    [AttributeType.date]: {
      icon: 'schedule',
      label: 'Date',
      parseRawValue: (d: string) => DateTime.fromISO(d).toISO(),
      prepValue: (v: string|string[]) => Array.isArray(v) ? v : [v],
      valueKey: 'value_date',
    },
    [AttributeType.boolean]: {
      icon: 'toggle_off',
      label: 'Toggle',
      parseRawValue: (v: any) => v || false,
      prepValue: (v: string|string[]) => Array.isArray(v) ? v : [v],
      valueKey: 'value_bool',
    },
  }

  @Attr({ type: 'string' }) label: string
  @Attr({ type: 'string' }) key: string
  @Attr({ type: 'number', minimum: 0, maximum: 8 }) type: AttributeType
  @Attr({ type: 'boolean', default: true }) editable?: boolean

  // Validation
  @Attr({ type: 'number' }) min?: number
  @Attr({ type: 'number' }) max?: number

  owner_id: string
}

export type AttributeDoc = RxDocument<AttributeSchema>
